import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';
import { breakpoints } from '../../design-system/tokens/breakpoints';
const slideIn = keyframes` from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } `;
const slideOut = keyframes` from { transform: translateX(0); opacity: 1; } to { transform: translateX(100%); opacity: 0; } `;
const Container = styled.div<{
  type: 'success' | 'error' | 'info' | 'warning';
  isClosing: boolean;
}>`
  position: relative;
  min-width: 0;
  width: 100%;
  max-width: 500px;
  padding: ${spacing[4]} ${spacing[5]};

  ${breakpoints.up.sm} {
    min-width: 300px;
  }
  border-radius: ${borders.radius.lg};
  box-shadow: ${shadows.xl};
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  animation: ${props => (props.isClosing ? slideOut : slideIn)}
    ${animations.duration.normal}ms ${animations.easing.easeOut};
  ${props => {
    const colors = props.theme.colors;
    switch (props.type) {
      case 'success':
        return ` background: ${colors.success}; color: ${colors.surface}; `;
      case 'error':
        return ` background: ${colors.error}; color: ${colors.surface}; `;
      case 'warning':
        return ` background: ${colors.warning}; color: ${colors.surface}; `;
      default:
        return ` background: ${colors.info}; color: ${colors.surface}; `;
    }
  }}
`;
const Icon = styled.span`
  font-size: ${typography.fontSize.xl};
  flex-shrink: 0;
`;
const Message = styled.div`
  flex: 1;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.normal};
`;
const CloseButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  font-size: ${typography.fontSize.xl};
  cursor: pointer;
  padding: 0;
  width: ${spacing[6]};
  height: ${spacing[6]};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  border-radius: ${borders.radius.sm};
  transition: ${animations.transition(
    ['opacity', 'background-color'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  flex-shrink: 0;
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.2);
  }
`;
export interface ToastProps {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose: (id: string) => void;
}
export const Toast: React.FC<ToastProps> = ({
  id,
  message,
  type,
  duration = 5000,
  onClose,
}) => {
  const theme = useTheme();
  const [isClosing, setIsClosing] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsClosing(true);
      setTimeout(() => onClose(id), animations.duration.normal);
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, id, onClose]);
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onClose(id), animations.duration.normal);
  };
  const icons = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };
  return (
    <Container
      type={type}
      isClosing={isClosing}
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      <Icon aria-hidden>{icons[type]}</Icon>
      <Message>{message}</Message>
      <CloseButton onClick={handleClose} aria-label="Close">
        ×
      </CloseButton>
    </Container>
  );
};
export default Toast;
